import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Price, PriceInput } from '@artosera/ui';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import type { GalerieSession } from '../../api/types.js';
import {
  useCompteArtiste,
  useConditionsArtiste,
  useSaisirFraisArtiste,
  type ConditionsArtiste,
} from '../../api/comptesArtistes.js';
import { Field } from '../Field.js';
import { EcrituresCompte, messageCompte } from '../comptes-artistes/compteUi.js';
import { ConditionsFormulaire } from '../comptes-artistes/ConditionsFormulaire.js';
import styles from '../comptes-artistes/ComptesArtistes.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function CompteArtisteSection(props: {
  galerieSlug: string;
  artisteId: string;
  galerie: GalerieSession;
}) {
  if (
    !galerieAModule(props.galerie, 'comptes-artistes') ||
    !galerieALaction(props.galerie, 'artist-accounts:read')
  )
    return null;
  return <CompteArtisteContenu {...props} />;
}
function CompteArtisteContenu({
  galerieSlug,
  artisteId,
  galerie,
}: {
  galerieSlug: string;
  artisteId: string;
  galerie: GalerieSession;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const label = (cle: string) => t(`regie:comptesArtistes.${cle}`);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const lecture = useCompteArtiste(galerieSlug, artisteId);
  const versions = useConditionsArtiste(galerieSlug, artisteId);
  const frais = useSaisirFraisArtiste(galerieSlug);
  const [edition, setEdition] = useState<{ version?: ConditionsArtiste } | null>(null);
  const [saisieFrais, setSaisieFrais] = useState(false);
  const [cle, setCle] = useState(() => crypto.randomUUID());
  const [montant, setMontant] = useState(0);
  const [libelle, setLibelle] = useState('');
  const [factureId, setFactureId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [erreur, setErreur] = useState('');
  const peutEcrire = galerieALaction(galerie, 'artist-accounts:write');
  const compte = lecture.data;
  const c = compte?.conditions;
  const aujourdHui = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(
    new Date(),
  );
  return (
    <section className={styles.section} aria-labelledby="compte-artiste-titre">
      <h2 id="compte-artiste-titre">{label('compte')}</h2>
      {lecture.isLoading && <p aria-busy="true">{t('commun:chargement')}</p>}
      {lecture.isError && (
        <p role="alert">{messageCompte(lecture.error, t('commun:erreurReseau'))}</p>
      )}
      {compte && (
        <>
          <p>
            {label('solde')} : <Price cents={compte.soldeCentimes} lang={langue} />
          </p>
          <section>
            <h3>{label('conditions')}</h3>
            {c ? (
              <dl className={styles.totaux}>
                <dt>{label('effetLe')}</dt>
                <dd>{c.effetLe.slice(0, 10)}</dd>
                <dt>{label('mode')}</dt>
                <dd>{label(`modes.${c.mode}`)}</dd>
                {c.mode === 'commission' && (
                  <>
                    <dt>{label('taux')}</dt>
                    <dd>{(c.tauxCommissionPdb ?? 0) / 100} %</dd>
                  </>
                )}
                <dt>{label('faitGenerateur')}</dt>
                <dd>{label(`faits.${c.faitGenerateur}`)}</dd>
                <dt>{label('periodicite')}</dt>
                <dd>{label(`periodicites.${c.periodicite}`)}</dd>
                <dt>{label('fraisParDefaut')}</dt>
                <dd>{c.fraisParDefaut || '—'}</dd>
                <dt>{label('contact')}</dt>
                <dd>
                  {c.contactId ? (
                    <Link to={`/${galerieSlug}/contacts/${c.contactId}`}>{c.contactId}</Link>
                  ) : (
                    '—'
                  )}
                </dd>
              </dl>
            ) : (
              <p>{label('conditionsAbsentes')}</p>
            )}
            <h3>{label('commissionContrat')}</h3>
            <p>{compte.commission ?? '—'}</p>
            <p>{label('commissionAide')}</p>
            {versions.isLoading && <p aria-busy="true">{t('commun:chargement')}</p>}
            {versions.isError && (
              <p role="alert">{messageCompte(versions.error, t('commun:erreurReseau'))}</p>
            )}
            <ul>
              {versions.data?.conditions.map((v) => (
                <li key={v.id}>
                  {v.effetLe.slice(0, 10)} · {label(`modes.${v.mode}`)}{' '}
                  {peutEcrire && v.effetLe.slice(0, 10) > aujourdHui && (
                    <Button size="sm" onClick={() => setEdition({ version: v })}>
                      {label('modifierVersion')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            <Verrouillable action="artist-accounts:write" ouvert={peutEcrire}>
              <Button onClick={() => setEdition({})}>{label('nouvelleVersion')}</Button>
            </Verrouillable>
          </section>
          <EcrituresCompte ecritures={compte.mouvements} />
          <section>
            <h3>{label('releves')}</h3>
            <ul>
              {compte.releves.map((r) => (
                <li key={r.id}>
                  <Link to={`/${galerieSlug}/comptes-artistes/releves/${r.id}`}>
                    {r.numero ?? label('etats.brouillon')} · {r.debut.slice(0, 10)} →{' '}
                    {r.fin.slice(0, 10)}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3>{label('avances')}</h3>
            <ul>
              {compte.avances.map((a) => (
                <li key={a.id}>
                  <Link to={`/${galerieSlug}/comptes-artistes/avances/${a.id}`}>
                    {label(`natures.${a.nature}`)} · {a.date.slice(0, 10)}
                  </Link>{' '}
                  · <Price cents={a.restantDuCentimes} lang={langue} />
                </li>
              ))}
            </ul>
          </section>
          <Verrouillable action="artist-accounts:write" ouvert={peutEcrire}>
            <Button
              onClick={() => {
                setSaisieFrais(true);
                setErreur('');
              }}
            >
              {label('saisirFrais')}
            </Button>
          </Verrouillable>
          {edition && (
            <Dialog
              open
              onOpenChange={(v) => {
                if (!v) setEdition(null);
              }}
              title={label(edition.version ? 'modifierVersion' : 'nouvelleVersion')}
              description={label('conditionsAide')}
              closeLabel={t('commun:fermer')}
            >
              <ConditionsFormulaire
                galerieSlug={galerieSlug}
                artisteId={artisteId}
                conditions={compte.conditions}
                {...edition}
                onEnregistre={() => setEdition(null)}
              />
            </Dialog>
          )}
          {saisieFrais && (
            <Dialog
              open
              onOpenChange={(v) => setSaisieFrais(v)}
              title={label('saisirFrais')}
              description={label('fraisAide')}
              closeLabel={t('commun:fermer')}
            >
              <form
                className={styles.formulaire}
                onSubmit={(e) => {
                  e.preventDefault();
                  setErreur('');
                  void frais
                    .mutateAsync({
                      artisteId,
                      montantCentimes: montant,
                      date,
                      cle,
                      libelle,
                      ...(factureId ? { factureId } : {}),
                    })
                    .then(() => {
                      setSaisieFrais(false);
                      setCle(crypto.randomUUID());
                      setMontant(0);
                      setLibelle('');
                      setFactureId('');
                    })
                    .catch((e: unknown) => setErreur(messageCompte(e, t('commun:erreurReseau'))));
                }}
              >
                <Field htmlFor="frais-montant" label={label('montant')}>
                  <PriceInput
                    id="frais-montant"
                    cents={montant}
                    onCentsChange={setMontant}
                    lang={langue}
                    required
                  />
                </Field>
                <Field htmlFor="frais-libelle" label={label('libelle')}>
                  <Input
                    id="frais-libelle"
                    required
                    maxLength={500}
                    value={libelle}
                    onChange={(e) => setLibelle(e.target.value)}
                  />
                </Field>
                <Field htmlFor="frais-date" label={label('date')}>
                  <Input
                    id="frais-date"
                    type="date"
                    required
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </Field>
                <Field htmlFor="frais-facture" label={label('factureId')}>
                  <Input
                    id="frais-facture"
                    value={factureId}
                    onChange={(e) => setFactureId(e.target.value)}
                  />
                </Field>
                {erreur && <p role="alert">{erreur}</p>}
                <Button
                  type="submit"
                  loading={frais.isPending}
                  disabled={montant <= 0 || !libelle.trim()}
                >
                  {label('enregistrerFrais')}
                </Button>
              </form>
            </Dialog>
          )}
        </>
      )}
    </section>
  );
}
